import React from 'react'

import { motion } from 'framer-motion'
import { CloseIcon } from '@chakra-ui/icons'
import {
  Button,
  chakra,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from '@chakra-ui/react'

import { buildAtsUrl } from '@/helpers/build-ats-url'
import {
  useInterview,
  useUpdateInterview,
} from '@/react-query/interview/interview'
import { useRole, useUserRoles } from '@/react-query/role'
import { useIsViewOnlyMember, useTeam } from '@/react-query/team'
import { ArrowSvg } from '@/views/assets/ArrowSvg'
import { PlusIcon } from '@/views/assets/PlusIcon'
import Card from '@/views/components/Card/Card'
import { EditRoleModal } from '@/views/screens/Interview/components/RoleCard/EditRoleModal'
import { InputATSJob } from '@/views/screens/Interview/components/RoleCard/InputATSJob'

export const RoleCard: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const isViewOnlyUser = useIsViewOnlyMember()
  const userRoles = useUserRoles()
  const role = useRole()
  const updateInterview = useUpdateInterview()
  const { data: interview } = useInterview()
  const interviewType = interview?.type
  const { data: team } = useTeam()
  const isATSConnected = !!team?.isATSConnected

  const atsJobUrl = buildAtsUrl({
    integrationType: team?.atsIntegrationType,
    resourceType: 'job',
    resourceId: role.data?.atsId || '',
  })

  return (
    <Card className="flex items-start flex-col px-[2.5rem] py-[1.8rem] mt-6 mb-6">
      <>
        <div className="flex items-center justify-between w-full">
          <Heading size={'sm'}>
            {interviewType === 'INTAKE' ? 'Role' : 'Job description'}
          </Heading>
          {atsJobUrl && (
            <chakra.a
              href={atsJobUrl}
              target="_blank"
              rel="noopener noreferrer"
              fontSize="xs"
              color="blue.500"
              display="flex"
              alignItems="center"
              gap={1}
              _hover={{ textDecoration: 'underline' }}
            >
              <Text as="span" aria-hidden>
                🔗
              </Text>
              View in ATS
            </chakra.a>
          )}
        </div>
        {role.data ? (
          <MotionBox
            display={'flex'}
            flexDirection={'column'}
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mt-5 flex flex-row items-center">
              <Text
                fontWeight={'bold'}
                fontSize={'1.25rem'}
                overflowWrap={'anywhere'}
              >
                {role.data?.jobTitle}
              </Text>
              {!isViewOnlyUser && (
                <IconButton
                  variant={'brand-solid-white'}
                  rounded={'full'}
                  ml={3}
                  aria-label={'Remove Role'}
                  icon={<CloseIcon />}
                  onClick={() => {
                    updateInterview.mutate({
                      roleId: null,
                    })
                  }}
                />
              )}
            </div>

            {!isViewOnlyUser && (
              <Button
                rounded={'1.25rem'}
                mt={5}
                onClick={onOpen}
                alignSelf={'flex-start'}
                variant={'brand-solid-white'}
              >
                Edit {interviewType === 'INTAKE' ? 'title' : 'job description'}
              </Button>
            )}
          </MotionBox>
        ) : (
          !isViewOnlyUser && (
            <div>
              {isATSConnected && <InputATSJob />}
              {interviewType !== 'INTAKE' && (
                <Menu>
                  <MenuButton
                    variant={'brand-solid-white'}
                    as={Button}
                    rounded={'full'}
                    rightIcon={<ArrowSvg />}
                    mt={5}
                    isDisabled={userRoles.data?.length === 0}
                  >
                    Select from saved
                  </MenuButton>

                  <MenuList maxHeight={'20rem'} overflow={'auto'}>
                    {[...(userRoles.data || [])]
                      .sort((a, b) => {
                        if (a.createdAt > b.createdAt) {
                          return -1
                        }
                        if (a.createdAt < b.createdAt) {
                          return 1
                        }

                        return 0
                      })
                      .map((role) => {
                        return (
                          <MenuItem
                            key={role.id}
                            onClick={() => {
                              updateInterview.mutate({
                                roleId: role.id,
                              })
                            }}
                          >
                            {role.jobTitle}
                          </MenuItem>
                        )
                      })}
                  </MenuList>
                </Menu>
              )}

              <Button
                variant={'brand-solid-white'}
                rounded={'full'}
                mt={5}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight={'bold'}
                onClick={(event) => {
                  event.preventDefault()
                  onOpen()
                }}
              >
                <PlusIcon stroke={'black'} />
                &nbsp;&nbsp;&nbsp;Create new...
              </Button>
            </div>
          )
        )}
        <EditRoleModal isOpen={isOpen} onClose={onClose} />
      </>
    </Card>
  )
}

const MotionBox = motion(chakra.div)
